import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  InputAccessoryView,
  Keyboard,
  Platform,
  Pressable,
  StyleSheet,
  View,
  type TextInput,
} from 'react-native';

import { colors, hit } from '../tokens';
import { Text } from './Text';

/**
 * **THE KEYBOARD, ONE PIECE FOR EVERY FIELD** (Evan, build 25 walk, 2026-09-25): *"there's no way to close the
 * keyboard, and the number pads have no return key at all. On iOS, add a bar above the keyboard with 'Done' on the
 * right that closes it, number pads included. Tapping outside a field also closes it. A text field's return key
 * moves to the next field, and the last one says Done. Build it as one shared piece used everywhere."*
 *
 * Three parts, each mounted or used in exactly one way:
 *  · `KeyboardDoneBar` — mounted ONCE at the app root. On iOS it is an `InputAccessoryView` with this id, and every
 *    field names the id (`TextField` does it itself), so the bar rides above every keyboard, number pads included.
 *    Android's keyboards carry their own close key, so there it draws nothing.
 *  · Tap-outside and drag-to-close are the SCROLL VIEWS' (`keyboardShouldPersistTaps="handled"`,
 *    `keyboardDismissMode="on-drag"`; `ScreenBody` defaults to both). An app-wide wrapper did it until the build-26
 *    walk, and it took the touches a scroll needed.
 *  · `FieldChain` — wraps a form of two or more fields; each field's return key says Next and moves on, the last
 *    says Done. A lone field needs no chain and says Done.
 *
 * `apps/mobile/src/keyboard.test.ts` fails if a field is added without these.
 */
export const KEYBOARD_DONE_ID = 'kk-keyboard-done';

export function KeyboardDoneBar({ label }: { label: string }) {
  if (Platform.OS !== 'ios') return null;
  return (
    <InputAccessoryView nativeID={KEYBOARD_DONE_ID}>
      <View style={styles.bar}>
        <Pressable
          onPress={() => Keyboard.dismiss()}
          accessibilityRole="button"
          accessibilityLabel={label}
          style={styles.done}
          testID="keyboard.done"
        >
          <Text variant="bodySmBold" tone="link">
            {label}
          </Text>
        </Pressable>
      </View>
    </InputAccessoryView>
  );
}

interface Chain {
  register: (ref: RefObject<TextInput | null>) => () => void;
  /** The field after this one, or null when it is the last. */
  next: (ref: RefObject<TextInput | null>) => RefObject<TextInput | null> | null;
  version: number;
}

const ChainContext = createContext<Chain | null>(null);

/**
 * The fields of one form, in the order they mount — which is the order they are drawn. A field that mounts later
 * (a conditional one) joins at the end, which is where such fields sit in every form this app has.
 */
export function FieldChain({ children }: { children: ReactNode }) {
  const order = useRef<RefObject<TextInput | null>[]>([]);
  const [version, setVersion] = useState(0);
  const register = useCallback((ref: RefObject<TextInput | null>) => {
    order.current = [...order.current, ref];
    setVersion((v) => v + 1);
    return () => {
      order.current = order.current.filter((r) => r !== ref);
      setVersion((v) => v + 1);
    };
  }, []);
  const next = useCallback((ref: RefObject<TextInput | null>) => {
    const at = order.current.indexOf(ref);
    return at === -1 ? null : (order.current[at + 1] ?? null);
  }, []);
  const value = useMemo(() => ({ register, next, version }), [register, next, version]);
  return <ChainContext.Provider value={value}>{children}</ChainContext.Provider>;
}

/**
 * What a field's return key does. In a chain and not the last: **Next**, and focus moves on without the keyboard
 * dropping. Otherwise: **Done**, and the keyboard closes. A multi-line field keeps its return for new lines.
 */
export function useReturnKey(ref: RefObject<TextInput | null>, multiline: boolean | undefined) {
  const chain = useContext(ChainContext);
  // On the STABLE `register`, never on `chain`: the chain's value changes each time a field joins, and re-running
  // this on that would unregister and re-register forever.
  const register = chain?.register;
  useLayoutEffect(() => (register ? register(ref) : undefined), [register, ref]);
  if (multiline) return {};
  const following = chain ? chain.next(ref) : null;
  return following
    ? {
        returnKeyType: 'next' as const,
        submitBehavior: 'submit' as const,
        onSubmitEditing: () => following.current?.focus(),
      }
    : {
        returnKeyType: 'done' as const,
        submitBehavior: 'blurAndSubmit' as const,
        onSubmitEditing: () => Keyboard.dismiss(),
      };
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    backgroundColor: colors.neutral,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.quietBorder,
  },
  done: { minHeight: hit.parent, justifyContent: 'center', paddingHorizontal: 16 },
});
